import React, { useMemo, useState } from "react";
import { CartProduct } from "@app/scripts/react-cart/components/CartProduct";
import { CartGwpPlaceholder } from "@app/scripts/react-cart/components/cartGwpPlaceholder";
import { CartCollection } from "@app/scripts/react-cart/components/CartCollection";
import { ProductRecommendations } from "@app/scripts/react-product-recommendations/components/ProductRecommendations";
import { useShopifyCart } from "@app/scripts/react-hooks/shopify";
import type { Banner } from "@app/scripts/types";
import { CartBanner } from "@app/scripts/react-cart/components/CartBanner";

type Props = {
  shopifyCart: ReturnType<typeof useShopifyCart>;
  lowStockThreshold: number;
  notifyLabel: string;
  youMayAlsoLikeHeading?: string;
  giftCarouselHeading?: string;
  youMayAlsoLikeCollection?: number[];
  youMayAlsoLikeProductsToShow: number;
  isCartMain?: boolean;
  banner?: Banner;
};

export const CartItems: React.FC<Props> = ({
  shopifyCart,
  lowStockThreshold,
  notifyLabel,
  youMayAlsoLikeHeading,
  giftCarouselHeading,
  youMayAlsoLikeCollection,
  youMayAlsoLikeProductsToShow,
  isCartMain,
  banner,
}) => {
  const {
    cart,
    giftIds,
    products,
    handleAdd,
    handleUpdate,
    addOosVariant,
    giftMessage,
    giftsInCart,
    overGiftThreshold,
    untilGiftThreshold,
    setError,
    gifts,
  } = shopifyCart;
  const isKid = window.location.href.includes("industriekids");
  const [active, setActive] = useState(true);
  const hasGiftsInCart = !!giftsInCart?.length;
  
  const visibleGiftIds = useMemo(() => gifts.filter(g => !g?.hide_in_carousel).map(p => p.id), [gifts])

  return cart && cart.items.length > 0 ? (
    <div className="cart__items">
      <div className="cart__items-inner">
        {overGiftThreshold && !hasGiftsInCart && (
          <div className="cart__recommendations free__gifts">
            <div className="text-brand-dark-grey mb-4 label" onClick={() => setActive(!active)}>
              {giftCarouselHeading}
            </div>
            <CartCollection
              productIds={visibleGiftIds}
              disabledDesktop={false}
              disabledMobile={false}
              columnsDesktop={isCartMain ? 2 : "auto"}
              columnsMobile={0.8}
              hasQuickAddDrawer={false}
              active
              addToCartBtns
              addAsGift
              arrowsMobile
              gifts={gifts}
            />
          </div>
        )}

        {giftIds && giftIds.length > 0 && !overGiftThreshold && !hasGiftsInCart ?
          <CartGwpPlaceholder
            productIds={giftIds}
            untilGiftThreshold={untilGiftThreshold}
          />
          : null
        }

        {cart.items.map((lineItem) => (
          <CartProduct
            key={lineItem.key}
            products={products}
            lineItem={lineItem}
            handleUpdate={handleUpdate}
            handleAdd={handleAdd}
            cart={cart}
            addOosVariant={addOosVariant}
            giftIds={giftIds}
            gifts={gifts}
            giftMessage={giftMessage}
            lowStockThreshold={lowStockThreshold}
            notifyLabel={notifyLabel}
            setError={setError}
            showProperties={isCartMain}
          />
        ))}
        {banner !== undefined && (<CartBanner banner={banner} />)}
      </div>

      {((hasGiftsInCart && giftIds?.length > 1) || giftIds?.length <= 1) && products?.length === 1 && (
        <div className="cart__recommendations">
          {youMayAlsoLikeHeading && (
            <div className="text-brand-dark-grey mb-4 label" onClick={() => setActive(!active)}>
              {youMayAlsoLikeHeading}
              <span>{active ? "-" : "+"}</span>
            </div>
          )}

          {youMayAlsoLikeCollection?.length ? (
            <CartCollection
              productIds={youMayAlsoLikeCollection}
              disabledDesktop={false}
              disabledMobile={false}
              columnsDesktop="auto"
              columnsMobile="auto"
            />
          ) : (
            <ProductRecommendations
              productId={products[0].id}
              productsToShow={youMayAlsoLikeProductsToShow}
              disabledDesktop={false}
              disabledMobile={false}
              columnsDesktop={isKid ? 1.7 : "auto"}
              columnsMobile={isKid ? 0.9 : "auto"}
              quickAddStyle="default"
              spaceBetween={16}
              active={active}
              isKid={isKid}
            />
          )}
        </div>
      )}
    </div>
  ) : (
    <div className="cart__items">
      <div className="cart__items-inner">
        {banner !== undefined && (<CartBanner banner={banner} />)}
        <div className="cart__items-empty text-md text-color-dark-grey text-center">Your cart is empty.</div>
      </div>
    </div>
  );
};
